import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Plus,
  RefreshCw,
  Loader2,
  Clock,
  Zap,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

// ── Diagnostic checks ───────────────────────────────────────────

interface DiagnosticResult {
  name: string;
  category: string;
  status: "pass" | "warn" | "fail";
  detail: string;
}

const runDiagnostics = async (): Promise<DiagnosticResult[]> => {
  const results: DiagnosticResult[] = [];

  // 1. Check RLS — can we query user_roles (admin-only)?
  try {
    const { error } = await supabase.from("user_roles").select("id").limit(1);
    results.push({
      name: "user_roles RLS",
      category: "RLS",
      status: error ? "fail" : "pass",
      detail: error ? error.message : "Admin can read user_roles",
    });
  } catch (e: any) {
    results.push({ name: "user_roles RLS", category: "RLS", status: "fail", detail: e.message });
  }

  // 2. Check profiles table
  try {
    const { error } = await supabase.from("profiles").select("id").limit(1);
    results.push({
      name: "profiles table",
      category: "Database",
      status: error ? "fail" : "pass",
      detail: error ? error.message : "Profiles accessible",
    });
  } catch (e: any) {
    results.push({ name: "profiles table", category: "Database", status: "fail", detail: e.message });
  }

  // 3. Check orders table
  try {
    const { error } = await supabase.from("orders").select("id").limit(1);
    results.push({
      name: "orders table",
      category: "Database",
      status: error ? "fail" : "pass",
      detail: error ? error.message : "Orders accessible",
    });
  } catch (e: any) {
    results.push({ name: "orders table", category: "Database", status: "fail", detail: e.message });
  }

  // 4. Check qa_issues table
  try {
    const { error } = await supabase.from("qa_issues").select("id").limit(1);
    results.push({
      name: "qa_issues table",
      category: "Database",
      status: error ? "fail" : "pass",
      detail: error ? error.message : "QA issues table accessible",
    });
  } catch (e: any) {
    results.push({ name: "qa_issues table", category: "Database", status: "fail", detail: e.message });
  }

  // 5. Check edge function connectivity (get-stripe-key as a lightweight ping)
  try {
    const { error } = await supabase.functions.invoke("get-stripe-key", { method: "POST" });
    results.push({
      name: "Edge Functions (get-stripe-key)",
      category: "Infrastructure",
      status: error ? "warn" : "pass",
      detail: error ? `Edge function error: ${error.message}` : "Edge functions responding",
    });
  } catch (e: any) {
    results.push({ name: "Edge Functions", category: "Infrastructure", status: "fail", detail: e.message });
  }

  // 6. Check storage bucket
  try {
    const { error } = await supabase.storage.from("email-assets").list("", { limit: 1 });
    results.push({
      name: "Storage (email-assets)",
      category: "Infrastructure",
      status: error ? "warn" : "pass",
      detail: error ? error.message : "Storage bucket accessible",
    });
  } catch (e: any) {
    results.push({ name: "Storage", category: "Infrastructure", status: "fail", detail: e.message });
  }

  // 7. Check referral link uses correct origin
  const origin = window.location.origin;
  const usesCorrectOrigin = !origin.includes("elarahealth.life") || origin === "https://elarahealth.life";
  results.push({
    name: "Referral link origin",
    category: "Links",
    status: "pass",
    detail: `Links use ${origin}`,
  });

  // 8. Check prescriptions table
  try {
    const { error } = await supabase.from("prescriptions").select("id").limit(1);
    results.push({
      name: "prescriptions table",
      category: "Database",
      status: error ? "fail" : "pass",
      detail: error ? error.message : "Prescriptions accessible",
    });
  } catch (e: any) {
    results.push({ name: "prescriptions table", category: "Database", status: "fail", detail: e.message });
  }

  return results;
};

// ── Severity badge ──────────────────────────────────────────────

const severityColor = (s: string) => {
  switch (s) {
    case "critical": return "destructive";
    case "high": return "destructive";
    case "medium": return "secondary";
    case "low": return "outline";
    default: return "secondary";
  }
};

const statusIcon = (s: string) => {
  switch (s) {
    case "pass": return <CheckCircle2 size={14} className="text-primary" />;
    case "warn": return <AlertTriangle size={14} className="text-accent-foreground" />;
    case "fail": return <XCircle size={14} className="text-destructive" />;
    default: return null;
  }
};

// ── Component ───────────────────────────────────────────────────

interface QAIssue {
  id: string;
  category: string;
  severity: string;
  title: string;
  description: string | null;
  affected_role: string | null;
  status: string;
  detected_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
}

const QADashboard = () => {
  const { user } = useAuth();
  const [issues, setIssues] = useState<QAIssue[]>([]);
  const [diagnostics, setDiagnostics] = useState<DiagnosticResult[]>([]);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter] = useState<"all" | "open" | "resolved">("open");

  // New issue form
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newCategory, setNewCategory] = useState("general");
  const [newSeverity, setNewSeverity] = useState("medium");
  const [newRole, setNewRole] = useState("");

  const fetchIssues = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from("qa_issues")
      .select("*")
      .order("detected_at", { ascending: false });

    if (filter !== "all") {
      query = query.eq("status", filter);
    }

    const { data } = await query;
    setIssues((data as QAIssue[]) ?? []);
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  const handleRunDiagnostics = async () => {
    setRunning(true);
    try {
      const results = await runDiagnostics();
      setDiagnostics(results);

      // Auto-log failures as QA issues
      const failures = results.filter((r) => r.status === "fail");
      for (const f of failures) {
        await supabase.from("qa_issues").insert({
          category: f.category,
          severity: "high",
          title: `[Auto] ${f.name} — FAIL`,
          description: f.detail,
          status: "open",
        });
      }
      if (failures.length > 0) {
        toast.warning(`${failures.length} issue(s) auto-logged`);
        fetchIssues();
      } else {
        toast.success("All diagnostics passed");
      }
    } catch {
      toast.error("Diagnostics failed to run");
    } finally {
      setRunning(false);
    }
  };

  const handleCreateIssue = async () => {
    if (!newTitle.trim()) return;
    const { error } = await supabase.from("qa_issues").insert({
      title: newTitle.trim(),
      description: newDescription.trim() || null,
      category: newCategory,
      severity: newSeverity,
      affected_role: newRole || null,
      status: "open",
    });
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Issue logged");
      setShowNew(false);
      setNewTitle("");
      setNewDescription("");
      setNewCategory("general");
      setNewSeverity("medium");
      setNewRole("");
      fetchIssues();
    }
  };

  const handleResolve = async (id: string) => {
    await supabase
      .from("qa_issues")
      .update({ status: "resolved", resolved_at: new Date().toISOString(), resolved_by: user?.id })
      .eq("id", id);
    fetchIssues();
    toast.success("Marked as resolved");
  };

  const handleReopen = async (id: string) => {
    await supabase
      .from("qa_issues")
      .update({ status: "open", resolved_at: null, resolved_by: null })
      .eq("id", id);
    fetchIssues();
  };

  const openCount = issues.filter((i) => i.status === "open").length;

  return (
    <div className="space-y-6">
      {/* Diagnostics */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap size={16} /> System Diagnostics
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={handleRunDiagnostics}
            disabled={running}
          >
            {running ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Run Checks
          </Button>
        </CardHeader>
        <CardContent>
          {diagnostics.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Click "Run Checks" to diagnose database, edge functions, storage, and link health.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {diagnostics.map((d, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 rounded-lg border border-border p-3 text-sm"
                >
                  {statusIcon(d.status)}
                  <div className="min-w-0">
                    <p className="font-medium truncate">{d.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{d.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Issue tracker */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle size={16} /> QA Issue Log
            {openCount > 0 && (
              <Badge variant="destructive" className="ml-1">
                {openCount} open
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={(v) => setFilter(v as any)}>
              <SelectTrigger className="w-[120px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" className="gap-1.5" onClick={() => setShowNew(true)}>
              <Plus size={14} /> Log Issue
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-muted-foreground" />
            </div>
          ) : issues.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No {filter === "all" ? "" : filter} issues found. Run diagnostics or log one manually.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Severity</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Detected</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {issues.map((issue) => (
                    <TableRow key={issue.id}>
                      <TableCell>
                        <Badge variant={severityColor(issue.severity) as any} className="capitalize text-xs">
                          {issue.severity}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[300px]">
                        <p className="font-medium text-sm truncate">{issue.title}</p>
                        {issue.description && (
                          <p className="text-xs text-muted-foreground truncate">{issue.description}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{issue.category}</TableCell>
                      <TableCell className="text-xs">{issue.affected_role || "—"}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        <Clock size={12} className="inline mr-1" />
                        {format(new Date(issue.detected_at), "MMM d, h:mm a")}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={issue.status === "open" ? "destructive" : "secondary"}
                          className="capitalize text-xs"
                        >
                          {issue.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {issue.status === "open" ? (
                          <Button size="sm" variant="ghost" onClick={() => handleResolve(issue.id)}>
                            Resolve
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => handleReopen(issue.id)}>
                            Reopen
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New issue dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log QA Issue</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Input
                placeholder="Issue title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
            </div>
            <div>
              <Textarea
                placeholder="Description (optional)"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Select value={newCategory} onValueChange={setNewCategory}>
                <SelectTrigger className="text-xs">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="RLS">RLS</SelectItem>
                  <SelectItem value="Database">Database</SelectItem>
                  <SelectItem value="Infrastructure">Infrastructure</SelectItem>
                  <SelectItem value="Links">Links</SelectItem>
                  <SelectItem value="UI">UI</SelectItem>
                  <SelectItem value="Auth">Auth</SelectItem>
                  <SelectItem value="Payments">Payments</SelectItem>
                </SelectContent>
              </Select>
              <Select value={newSeverity} onValueChange={setNewSeverity}>
                <SelectTrigger className="text-xs">
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className="text-xs">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="patient">Patient</SelectItem>
                  <SelectItem value="provider">Provider</SelectItem>
                  <SelectItem value="salesperson">Sales</SelectItem>
                  <SelectItem value="pharmacy">Pharmacy</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateIssue} disabled={!newTitle.trim()}>
              Log Issue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QADashboard;
